import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HardDriveDownload, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const quando = (iso) => new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });

export default function BackupSection() {
  const [stato, setStato] = useState(null);
  const [inCorso, setInCorso] = useState(false);

  const carica = useCallback(() => {
    api.get('/backup/stato').then(r => setStato(r.data)).catch(() => {});
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const esegui = async () => {
    setInCorso(true);
    try {
      const r = await api.post('/backup/esegui');
      toast.success(`Backup completato: ${r.data.dimensione_mb} MB, ${r.data.collezioni} collezioni`);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || 'Backup fallito'); }
    setInCorso(false);
  };

  if (!stato) return null;
  const ultimo = stato.ultimo_esito;

  return (
    <Card data-testid="backup-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <HardDriveDownload className="w-5 h-5 text-slate-600" />Backup del database
          <Badge className="bg-emerald-100 text-emerald-700 text-[10px] gap-1"><Clock className="w-3 h-3" />automatico ogni notte alle {stato.orario_job}</Badge>
          <Button size="sm" className="ml-auto h-8 text-xs bg-brand hover:bg-brand-dark text-white"
            onClick={esegui} disabled={inCorso} data-testid="backup-adesso-btn">
            {inCorso ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Backup in corso...</> : 'Backup adesso'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Ultimo backup</p>
            <p className="text-sm font-bold text-slate-700" data-testid="backup-ultimo">
              {stato.ultimo_backup ? quando(stato.ultimo_backup.created_at) : 'Mai eseguito'}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Esito</p>
            <p className="text-sm font-bold flex items-center gap-1">
              {ultimo?.ok ? <><CheckCircle className="w-4 h-4 text-emerald-600" /><span className="text-emerald-700">Riuscito</span></>
                : ultimo ? <><XCircle className="w-4 h-4 text-red-600" /><span className="text-red-700">Fallito</span></>
                  : <span className="text-slate-500">-</span>}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Copie conservate</p>
            <p className="text-sm font-bold text-slate-700" data-testid="backup-copie">{stato.backup_presenti.length} / {stato.da_conservare}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Spazio occupato</p>
            <p className="text-sm font-bold text-slate-700">{stato.spazio_totale_mb} MB</p>
          </div>
        </div>
        {ultimo && !ultimo.ok && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{ultimo.errore}</p>}
        <p className="text-[11px] text-slate-400">Cartella: {stato.cartella} · vengono conservate le ultime {stato.da_conservare} copie.</p>
        {stato.storico.length > 0 && (
          <div className="space-y-1.5">
            {stato.storico.slice(0, 5).map(b => (
              <div key={b.id} className="flex items-center justify-between text-xs border rounded-lg px-3 py-2" data-testid={`backup-log-${b.id}`}>
                <span className="text-slate-600">{quando(b.created_at)} · {b.automatico ? 'automatico' : `manuale (${b.richiesto_da})`}</span>
                <span className="flex items-center gap-2 text-slate-500">
                  {b.ok ? `${b.dimensione_mb} MB · ${b.collezioni} collezioni` : 'errore'}
                  {b.ok ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-red-600" />}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
